import { Form, Input } from "decorator-form";
import { Entity, Field } from "typegraph";

import EntitySelect from "common/form/fields/entitySelect/EntitySelect";
import InputType from "common/form/InputType";
import IdName from "core/util/IdName";
import { ArticleContributor } from "../articleContributor/entities";
import entityMeta from "../entityMeta";
import Playlist from "../entities/Playlist";
import { IFileUpload } from "common/form/fields/upload/UploadField";

class File {
	@Field()
	public id: any;

	@Field()
	public displayName: string;

	@Field()
	public description: string;

	@Field()
	public name: string;

	@Field()
	public url: string;

	@Field()
	public contentType: string;
}

@Entity(entityMeta.ATTACHMENT)
export class Attachment {
	@Field()
	public id: any;

	@Field()
	public fileUrl: string;

	@Input({
		type: InputType.UPLOAD,
		title: "File",
		required: true,
	})
	public tempUpload?: IFileUpload;

	@Field({ entity: File })
	public file: File;

	@Input({ type: InputType.TEXT, title: "Name", required: true })
	public fileDisplayName?: string;

	@Input({ type: InputType.TEXT_HTML, title: "Description" })
	public fileDescription?: string;
}

@Entity(entityMeta.CONTENT_BLOCK)
export class ContentBlock {
	@Field()
	public id: any;

	@Field()
	public guid: string;

	@Field()
	@Input({ type: InputType.TEXT, title: "Name" })
	public name: string;

	@Field()
	@Input({ type: InputType.TEXT_HTML, title: "Text" })
	public text: string;

	@Field()
	@Input({ type: InputType.UPLOAD_URL, title: "File" })
	public fileUrl: string;
}

class User {
	@Field() public id: any;
	@Field() public firstName: string;
	@Field() public lastName: string;
}

@Entity(entityMeta.CATEGORY)
class Category {
	@Field() public id: any;
	@Field() public name: string;
}

class ArticleStat {
	@Field() public id: any;
	@Field() public views: number;
}

const categoryMultiSelect = EntitySelect.registered<Category, Category, "many">(
	{
		entity: Category,
		type: "many",
		getValue: (c) => c,
		equals: (c1, c2) => c1.id === c2.id,
		getDefaultValueParams: (categories) => {
			return { ids: categories.map((c) => c.id) };
		},
	},
	"array"
);

@Entity(entityMeta.TAGS)
class Tag {
	@Field() public id: any;
	@Field() public name: string;
}

const tagMultiSelect = EntitySelect.registered<Tag, Tag, "many">(
	{
		entity: Tag,
		type: "many",
		getValue: (t) => t,
		equals: (t1, t2) => t1.id === t2.id,
		getDefaultValueParams: (tags) => {
			return { ids: tags.map((t) => t.id) };
		},
	},
	"array"
);

@Entity(entityMeta.ARTICLE)
@Form()
export class Article<T = any> {
	@Field()
	public id: any;

	@Field()
	public guid?: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Name",
		description: "Internal Name (unique)",
		required: true,
	})
	public name: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Display Name",
		description: "End user-facing name",
	})
	public displayName?: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Keywords",
	})
	public keywords: string;

	@Field()
	@Input({ type: InputType.TEXT_HTML, title: "Description" })
	public description: string;

	@Field()
	public iconUrl?: string;

	@Input({ type: InputType.UPLOAD_URL_IMAGE, title: "Icon" })
	public tempIconUrl?: string;

	@Field()
	public uiSchema?: { type: string; data: T }; // loaded as string, parse JSON before setting into state, convert back before saving

	@Field({ entity: IdName })
	@Input({ type: tagMultiSelect, title: "Tags" })
	public tags?: null | IdName[];

	@Field({ entity: IdName })
	@Input({ type: categoryMultiSelect, title: "Categories" })
	public categories?: null | IdName[];

	@Field({ entity: ContentBlock })
	public contentBlocks?: null | ContentBlock[];

	@Field({ entity: User })
	public owner?: User;

	@Field({ entity: ArticleContributor })
	public contributors?: null | ArticleContributor[];

	@Field({ entity: Attachment })
	public attachments?: null | Attachment[];

	// @Field({entity: ArticleStat})
	public stat?: null | ArticleStat;

	@Field({ entity: Playlist })
	public playlist?: Playlist;
}



// WEBPACK FOOTER //
// ./src/panels/articleDetails/article/entities.ts